// src/pages/Careers.jsx
import React from "react";

export function Careers() {
    const openPositions = [
        {
            title: 'Senior Frontend Developer',
            department: 'Engineering',
            location: 'San Francisco, CA / Remote',
            type: 'Full-time',
            description: 'Help build the future of social media management tools with React and modern web technologies.'
        },
        {
            title: 'AI/ML Engineer',
            department: 'Engineering',
            location: 'San Francisco, CA / Remote',
            type: 'Full-time',
            description: 'Work on cutting-edge AI models for content generation and social media optimization.'
        },
        {
            title: 'Product Designer',
            department: 'Design',
            location: 'San Francisco, CA / Remote',
            type: 'Full-time',
            description: 'Design intuitive interfaces that make social media management delightful for users.'
        }
    ];

    return (
        <>
            <SEO
                title="Careers at PostMgr - Join Our Team"
                description="Join PostMgr and help build the future of AI-powered social media management. View open positions and apply today."
                url="https://postmgr.com/careers"
            />

            <div className="min-h-screen bg-gray-50">
                <nav className="bg-white shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between h-16">
                            <div className="flex items-center">
                                <Link to="/" className="flex items-center">
                                    <img src="/images/Bob_only.svg" alt="PostMgr" className="h-8 w-8" />
                                    <span className="ml-2 text-2xl font-bold text-blue-600">PostMgr</span>
                                </Link>
                            </div>
                            <div className="flex items-center space-x-4">
                                <Link to="/login" className="text-gray-700 hover:text-blue-600">Sign In</Link>
                                <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">Get Started</Link>
                            </div>
                        </div>
                    </div>
                </nav>

                {/* Hero Section */}
                <div className="bg-blue-600 text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl font-bold mb-4">Join Our Mission</h1>
                        <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                            Help us democratize social media marketing through AI and build tools that empower businesses worldwide.
                        </p>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    {/* Company Culture */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Work at PostMgr?</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl">🚀</span>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation First</h3>
                                <p className="text-gray-600">Work with cutting-edge AI technology and shape the future of social media marketing.</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl">🌍</span>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Remote-First</h3>
                                <p className="text-gray-600">Work from anywhere with flexible hours and a strong remote culture.</p>
                            </div>
                            <div className="text-center">
                                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl">💫</span>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Growth Focused</h3>
                                <p className="text-gray-600">Continuous learning opportunities and career development support.</p>
                            </div>
                        </div>
                    </div>

                    {/* Open Positions */}
                    <div className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Open Positions</h2>
                        <div className="space-y-6">
                            {openPositions.map((position, index) => (
                                <div key={index} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                        <div className="flex-1">
                                            <h3 className="text-xl font-semibold text-gray-900 mb-2">{position.title}</h3>
                                            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                                                <span>📍 {position.location}</span>
                                                <span>🏢 {position.department}</span>
                                                <span>⏰ {position.type}</span>
                                            </div>
                                            <p className="text-gray-600">{position.description}</p>
                                        </div>
                                        <div className="mt-4 md:mt-0 md:ml-6">
                                            <Link
                                                to="/contact"
                                                className="inline-flex items-center px-4 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50"
                                            >
                                                Apply Now
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Benefits */}
                    <div className="bg-white rounded-lg shadow-sm p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Benefits & Perks</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <ul className="space-y-3">
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-3">✓</span>
                                    Competitive salary and equity
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-3">✓</span>
                                    Health, dental, and vision insurance
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-3">✓</span>
                                    Unlimited PTO policy
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-3">✓</span>
                                    $2,000 annual learning budget
                                </li>
                            </ul>
                            <ul className="space-y-3">
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-3">✓</span>
                                    Remote work stipend
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-3">✓</span>
                                    Latest tech equipment
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-3">✓</span>
                                    Team retreats and events
                                </li>
                                <li className="flex items-center">
                                    <span className="text-green-500 mr-3">✓</span>
                                    401(k) with company matching
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

// Export all components
export default Careers;